/**
 * Trainer performance — the comparable headline numbers and how each one is
 * SHOWN: its label, its format, the precision a reader sees, which way is
 * better, and the change since last month worked out on the shown values.
 *
 * One definition for every surface that prints these numbers: the
 * `/trainer-performance` page (the SPA aliases this file, `vite.config.ts`),
 * the monthly review PDF (`canonical-actions/src/trainer-reviews/`) and the
 * owners' briefing before a review call (`trainer-review-call.ts`).
 * Before 2026-10-23 it lived in the SPA's `lib/trainer-performance.ts`; the
 * PDF would otherwise have needed a second copy of every label, and the two
 * would drift.
 *
 * A dependency-free leaf, imported by deep subpath: the SPA cannot import the
 * canonical packages' barrels. Every definition BEHIND the numbers is the
 * report's (`canonical-actions/src/trainer-performance/compute.ts`).
 */

/** The comparable numbers — one per key — that the team medians are taken over. */
export const TRAINER_PERFORMANCE_HEADLINE_KEYS = [
  "delivered",
  "hours",
  "occupancy_pct",
  "active_clients",
  "new_clients",
  "retention_pct",
  "rating_average",
  "rating_count",
  "trials",
  "trial_conversion_pct_3m",
  "signups",
  "repeat_purchases",
  "postsession_pct",
  "consultation_pct",
  "pay_total_minor",
  "bonus_minor",
  "revenue_minor",
  "pay_to_revenue_pct",
  "late_cancels",
  "no_shows",
] as const;

export type TrainerPerformanceHeadlineKey =
  (typeof TRAINER_PERFORMANCE_HEADLINE_KEYS)[number];

export type TrainerPerformanceHeadline = Readonly<
  Record<TrainerPerformanceHeadlineKey, number | null>
>;

// ---------------------------------------------------------------------------
// Formatting. Pounds, as on `/payroll` — the payroll engine is GBP.
// ---------------------------------------------------------------------------

const GBP_WHOLE = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});
const GBP_EXACT = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

export function money(minor: number | null, exact = false): string {
  if (minor === null) return "—";
  return (exact ? GBP_EXACT : GBP_WHOLE).format(minor / 100);
}

export function pct(v: number | null): string {
  return v === null ? "—" : `${v}%`;
}

export function count(v: number | null): string {
  return v === null ? "—" : String(v);
}

/**
 * A rating's number alone, as `rating` prints it: "2.9". Rounded half-up
 * before printing, as `shown` rounds it: `toFixed` alone rounds the binary
 * float, so a stored 2.15 (2.1499…) printed "2.1" while its change since last
 * month counted it as 2.2. Mario, 2026-11-02: "Exact half-up everywhere".
 */
export function ratingFigure(v: number): string {
  return roundTo(v, 1).toFixed(1);
}

export function rating(v: number | null): string {
  return v === null ? "—" : `${ratingFigure(v)} / 3`;
}

/** `v` rounded to `places` decimals. */
export function roundTo(v: number, places: number): number {
  const f = 10 ** places;
  return Math.round(v * f) / f;
}

// ---------------------------------------------------------------------------
// The comparable numbers.
// ---------------------------------------------------------------------------

/** Which way is better — colours a change since last month. */
export type Better = "higher" | "lower" | "neither";

export interface Metric {
  readonly label: string;
  readonly format: (v: number | null) => string;
  /**
   * The value at the precision the page shows it. A change since last month
   * is the difference of two SHOWN values, so it always agrees with the two
   * figures a reader can subtract — never a "−0" between two equal-looking
   * ratings, or "+£0" for a few pence.
   */
  readonly shown: (v: number) => number;
  /** Formats a non-zero change since last month ("+4", "−5.2 pts", "+£120"). */
  readonly delta: (d: number) => string;
  readonly better: Better;
}

const signed = (d: number, unit = ""): string =>
  `${d > 0 ? "+" : "−"}${Math.abs(d)}${unit}`;

const COUNT_METRIC = (label: string, better: Better = "higher"): Metric => ({
  label,
  format: count,
  shown: (v) => v,
  delta: (d) => signed(d),
  better,
});
const PCT_METRIC = (label: string): Metric => ({
  label,
  format: pct,
  shown: (v) => roundTo(v, 1),
  delta: (d) => signed(d, " pts"),
  better: "higher",
});
const MONEY_METRIC = (label: string): Metric => ({
  label,
  format: (v) => money(v),
  // Whole pounds, as `money` shows them.
  shown: (v) => Math.round(v / 100) * 100,
  delta: (d) => `${d > 0 ? "+" : "−"}${money(Math.abs(d))}`,
  better: "neither",
});

export const METRICS: Readonly<Record<TrainerPerformanceHeadlineKey, Metric>> = {
  delivered: COUNT_METRIC("Sessions"),
  hours: {
    ...COUNT_METRIC("Hours"),
    format: (v) => (v === null ? "—" : `${v}h`),
    shown: (v) => roundTo(v, 2),
  },
  occupancy_pct: PCT_METRIC("Occupancy"),
  active_clients: COUNT_METRIC("Clients"),
  new_clients: COUNT_METRIC("New clients"),
  retention_pct: PCT_METRIC("Retention"),
  rating_average: {
    label: "Rating",
    format: rating,
    shown: (v) => roundTo(v, 1),
    delta: (d) => signed(d),
    better: "higher",
  },
  rating_count: COUNT_METRIC("Ratings"),
  trials: COUNT_METRIC("Trials"),
  trial_conversion_pct_3m: PCT_METRIC("Trial → sign-up (3 mo)"),
  signups: COUNT_METRIC("Sign-ups"),
  repeat_purchases: COUNT_METRIC("Repeat purchases"),
  postsession_pct: PCT_METRIC("Post-session replies"),
  consultation_pct: PCT_METRIC("Consultation on file"),
  pay_total_minor: MONEY_METRIC("Pay"),
  bonus_minor: MONEY_METRIC("Bonus"),
  revenue_minor: MONEY_METRIC("Revenue"),
  pay_to_revenue_pct: { ...PCT_METRIC("Pay ÷ revenue"), better: "neither" },
  late_cancels: COUNT_METRIC("Late cancels", "lower"),
  no_shows: COUNT_METRIC("No-shows", "lower"),
};

// ---------------------------------------------------------------------------
// Change since last month.
// ---------------------------------------------------------------------------

export interface MonthChange {
  /** Shown-value difference; 0 reads "no change". */
  readonly d: number;
  readonly tone: "good" | "poor" | "neutral";
}

/** null when either month has no figure — there is nothing to compare. */
export function changeSinceLastMonth(
  metric: Metric,
  now: number | null,
  before: number | null,
): MonthChange | null {
  if (now === null || before === null) return null;
  // Shown again after subtracting: two one-decimal values differ by a float
  // like 0.09999999999999964. `+ 0` turns a −0 into 0.
  const d = metric.shown(metric.shown(now) - metric.shown(before)) + 0;
  const tone =
    d === 0 || metric.better === "neither"
      ? "neutral"
      : (d > 0) === (metric.better === "higher")
        ? "good"
        : "poor";
  return { d, tone };
}
